import { useCallback, useEffect, useMemo, useRef } from 'react';

const DEFAULT_MAX = 16;
const SPRING = 0.2;
const FRICTION = 0.76;
const PRESS_BUMP = 10;

/**
 * Visible pointer-driven 3D tilt (scene perspective + card rotateX/Y).
 * Uses both native listeners and React handlers so portal/modals always tilt.
 */
export function use3DTilt({
  maxTilt = DEFAULT_MAX,
  enabled = true,
  perspective = 640,
} = {}) {
  const sceneNode = useRef(null);
  const cardNode = useRef(null);
  const cleanRef = useRef(null);
  const apiRef = useRef(null);
  const optsRef = useRef({ maxTilt, enabled, perspective });
  optsRef.current = { maxTilt, enabled, perspective };

  const detach = useCallback(() => {
    if (cleanRef.current) {
      cleanRef.current();
      cleanRef.current = null;
    }
    apiRef.current = null;
  }, []);

  const bind = useCallback(() => {
    detach();
    const scene = sceneNode.current;
    const card = cardNode.current || scene;
    if (!scene || !card || typeof window === 'undefined') return;

    const reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    let raf = 0;
    let hovering = false;
    let pressing = false;
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    const velocity = { x: 0, y: 0 };
    const shine = { x: 50, y: 42 };

    scene.style.perspective = `${optsRef.current.perspective}px`;
    scene.style.perspectiveOrigin = '50% 42%';
    card.style.transformStyle = 'preserve-3d';
    card.style.willChange = 'transform';
    card.style.transformOrigin = '50% 42%';

    const apply = () => {
      const scale = pressing ? 0.97 : 1;
      card.style.setProperty(
        'transform',
        `rotateX(${current.x.toFixed(2)}deg) rotateY(${current.y.toFixed(2)}deg) scale(${scale})`,
        'important',
      );
      card.style.setProperty('--tilt-shine-x', `${shine.x}%`);
      card.style.setProperty('--tilt-shine-y', `${shine.y}%`);
      const max = optsRef.current.maxTilt;
      card.style.setProperty('--tilt-depth', `${(Math.abs(current.x) + Math.abs(current.y)) / (max * 2)}`);
    };

    const isReduced = () => reducedMq.matches || !optsRef.current.enabled;

    const clamp = (value) => {
      const max = optsRef.current.maxTilt;
      return Math.max(-max, Math.min(max, value));
    };

    const tick = () => {
      const dx = target.x - current.x;
      const dy = target.y - current.y;
      velocity.x = velocity.x * FRICTION + dx * SPRING;
      velocity.y = velocity.y * FRICTION + dy * SPRING;
      current.x += velocity.x;
      current.y += velocity.y;
      apply();
      if (
        Math.abs(dx) > 0.03
        || Math.abs(dy) > 0.03
        || Math.abs(velocity.x) > 0.03
        || Math.abs(velocity.y) > 0.03
        || pressing
      ) {
        raf = requestAnimationFrame(tick);
      } else {
        raf = 0;
        current.x = target.x;
        current.y = target.y;
        apply();
      }
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const setTarget = (x, y) => {
      target.x = clamp(x);
      target.y = clamp(y);
      schedule();
    };

    const pointFromEvent = (event) => {
      const rect = card.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      return {
        px: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
        py: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
      };
    };

    const onMove = (event) => {
      if (isReduced()) return;
      const point = pointFromEvent(event);
      if (!point) return;
      const max = optsRef.current.maxTilt;
      shine.x = point.px * 100;
      shine.y = point.py * 100;
      setTarget((0.5 - point.py) * max * 2, (point.px - 0.5) * max * 2);
      hovering = true;
    };

    const onLeave = () => {
      hovering = false;
      pressing = false;
      shine.x = 50;
      shine.y = 42;
      setTarget(0, 0);
    };

    const onDown = (event) => {
      if (isReduced()) return;
      if (event.target?.closest?.('button, a, input, [data-no-tilt-press]')) return;
      const point = pointFromEvent(event);
      if (!point) return;
      pressing = true;
      shine.x = point.px * 100;
      shine.y = point.py * 100;
      setTarget((0.5 - point.py) * PRESS_BUMP, (point.px - 0.5) * PRESS_BUMP);
      schedule();
    };

    const onUp = () => {
      pressing = false;
      if (!hovering) setTarget(0, 0);
      else schedule();
    };

    const onDocMove = (event) => {
      const rect = scene.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left
        && event.clientX <= rect.right
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom;
      if (inside) onMove(event);
      else if (hovering) onLeave();
    };

    // Document-level tracking is reliable for portal modals / synthetic events.
    document.addEventListener('pointermove', onDocMove, { passive: true });
    document.addEventListener('mousemove', onDocMove, { passive: true });
    scene.addEventListener('pointerdown', onDown);
    scene.addEventListener('mousedown', onDown);
    scene.addEventListener('pointerup', onUp);
    scene.addEventListener('mouseup', onUp);
    scene.addEventListener('pointerleave', onLeave);
    scene.addEventListener('mouseleave', onLeave);
    scene.addEventListener('pointercancel', onLeave);

    apiRef.current = { onMove, onLeave, onDown, onUp, apply, setTarget };
    apply();

    cleanRef.current = () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('pointermove', onDocMove);
      document.removeEventListener('mousemove', onDocMove);
      scene.removeEventListener('pointerdown', onDown);
      scene.removeEventListener('mousedown', onDown);
      scene.removeEventListener('pointerup', onUp);
      scene.removeEventListener('mouseup', onUp);
      scene.removeEventListener('pointerleave', onLeave);
      scene.removeEventListener('mouseleave', onLeave);
      scene.removeEventListener('pointercancel', onLeave);
      apiRef.current = null;
    };
  }, [detach]);

  const sceneRef = useCallback((node) => {
    sceneNode.current = node;
    if (node) bind();
    else detach();
  }, [bind, detach]);

  const cardRef = useCallback((node) => {
    cardNode.current = node;
    if (sceneNode.current || node) bind();
    if (!node && !sceneNode.current) detach();
  }, [bind, detach]);

  const tiltRef = useCallback((node) => {
    sceneNode.current = node;
    cardNode.current = node;
    if (node) bind();
    else detach();
  }, [bind, detach]);

  const tiltHandlers = useMemo(() => ({
    onMouseMove: (event) => apiRef.current?.onMove(event),
    onPointerMove: (event) => apiRef.current?.onMove(event),
    onMouseLeave: () => apiRef.current?.onLeave(),
    onPointerLeave: () => apiRef.current?.onLeave(),
    onMouseDown: (event) => apiRef.current?.onDown(event),
    onPointerDown: (event) => apiRef.current?.onDown(event),
    onMouseUp: () => apiRef.current?.onUp(),
    onPointerUp: () => apiRef.current?.onUp(),
  }), []);

  useEffect(() => () => detach(), [detach]);

  return {
    sceneRef,
    cardRef,
    tiltRef,
    tiltHandlers,
  };
}
